import * as React from 'react';

import { cn } from '@/ux-common/lib/utils';

/**
 * Typography component — minimal polymorphic wrapper for text elements.
 *
 * All visual styling is applied via CSS classes. This wrapper provides:
 * - Polymorphic element selection via the `as` prop (default: `'p'`)
 * - `data-component="Typography"` for component identification (consumer can override)
 * - Ref forwarding to the underlying chosen element, typed per element
 * - `className` merging via `cn()`
 * - Full HTML attribute passthrough (id, aria-*, role, onClick, etc.) typed per element
 *
 * Typography ships ALL text-styling classes — semantic roles you pick by
 * content: `.text-display` (display); several header tiers
 * (`.text-header-page` / `-section` / `-card`) and eyebrows
 * (`.text-header-count` / `-sub` / `-nav`); body (`.text-body-base` / `-sm`);
 * and `.text-caption`. Detailed per-role usage guidance is owned by design.
 * Color comes from the active theme — use theme tokens.
 *
 * These tokens are the raw **reading hierarchy** only. There is no label
 * token — a label belongs to a component, so each component styles its own
 * label with Tailwind. Non-reading components (button, input, chip…) use
 * Tailwind too; the maintainer owns keeping their sizes cohesive.
 *
 * Weight and case are baked to each role's default. To vary, add Tailwind
 * utilities yourself — there are NO `-bold` / `-caps` classes:
 * - bold: `className="text-body-base font-bold"`
 * - uppercase: `className="text-body-base uppercase"`
 * - partial emphasis: wrap the run in a `<span>` with the same class + utility.
 *
 * Previous tokens were named incorrectly. Old names (`.text-body`,
 * `.text-page-header`, `.text-category-header`, `.text-sub-header`,
 * `.text-card-header`, `.text-nav-header`, `.text-count-header`) remain as
 * deprecated aliases for compatibility — prefer the corrected names.
 *
 * For heading-tier text inside a layout, compose Typography inside
 * `<Header>` — Header provides the flex shell, Typography supplies
 * both the element (`as="h1"` etc.) and the visual style.
 *
 * NOTE: `as` overrides the rendered element but NOT the semantic role.
 * For true headings, pass `as="h1"` etc. — do not use `as="div"` for
 * heading-styled content (screen readers lose the heading landmark).
 *
 * @example
 * ```tsx
 * // Page heading composed with Header
 * <Header>
 *   <Typography as="h1" className="header-title text-header-page">Trends</Typography>
 * </Header>
 *
 * // Body paragraph
 * <Typography as="p" className="text-body-base">
 *   Compressor discharge pressure exceeded the alarm threshold.
 * </Typography>
 *
 * // Bold via composed utility (no -bold class)
 * <Typography as="p" className="text-body-base font-bold">Critical</Typography>
 *
 * // Inline caption
 * <Typography as="span" className="text-caption">Last 30 days</Typography>
 *
 * // Custom element with native typing — `href` is typed against <a>
 * <Typography as="a" href="/docs" className="text-body-base underline">
 *   Read the docs
 * </Typography>
 * ```
 */

type TypographyOwnProps<T extends React.ElementType> = {
  as?: T;
  className?: string;
  children?: React.ReactNode;
};

export type TypographyProps<T extends React.ElementType = 'p'> = TypographyOwnProps<T> &
  Omit<React.ComponentPropsWithoutRef<T>, keyof TypographyOwnProps<T>>;

type PolymorphicRef<T extends React.ElementType> = React.ComponentPropsWithRef<T>['ref'];

type TypographyComponent = <T extends React.ElementType = 'p'>(
  props: TypographyProps<T> & { ref?: PolymorphicRef<T> }
) => React.ReactElement | null;

export const Typography: TypographyComponent = React.forwardRef(
  <T extends React.ElementType = 'p'>(
    { as, className, children, ...props }: TypographyProps<T>,
    ref: React.Ref<Element>
  ) => {
    const Component = (as ?? 'p') as React.ElementType;
    return (
      <Component ref={ref} data-component="Typography" className={cn(className)} {...props}>
        {children}
      </Component>
    );
  }
) as TypographyComponent;

(Typography as React.FC).displayName = 'Typography';
